// How many times the assembler has saved a recording as an INCOMPLETE partial
// while keeping its local segments queued for a complete retry.
//
// The assembler keeps the segment rows when a gap is rescuable — the server's
// backstop lists the missing indices but the byte pull failed (a dropped
// connection, a timeout) — so a later drain pass can pull them and upload a
// COMPLETE recording that supersedes the partial. Without a bound that retry is
// forever: a pull that never succeeds would re-stitch and re-upload the same
// partial on every backoff tick, moving megabytes of PHI for a result that cannot
// improve. After MAX_PARTIAL_ATTEMPTS the assembler accepts the gap, deletes the
// rows, and stops.
//
// Mirrors recordingRecoveryState.js: viewer-scoped key, capped entry count,
// silent on any storage failure (a counter is best-effort — losing it costs at
// most a few extra attempts, never audio).
import { recordingViewerKey } from "./recordingViewerKey";

const PREFIX = "recordingPartialAttempts:";
const MAX_IDS = 50;

// Three complete-retry attempts across the drain's 20s→5min backoff spans several
// minutes of connectivity — enough to ride out a dropped connection, short enough
// that a permanently unreachable segment doesn't loop.
export const MAX_PARTIAL_ATTEMPTS = 3;

function storeKey() {
  return PREFIX + recordingViewerKey();
}

function readMap() {
  try {
    const raw = localStorage.getItem(storeKey());
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeMap(map) {
  try {
    const keys = Object.keys(map);
    if (keys.length > MAX_IDS) {
      // Shed the least-attempted ids first — the ones close to the cap are the
      // ones whose count is still load-bearing.
      keys.sort((a, b) => (map[a] || 0) - (map[b] || 0));
      for (let i = 0; i < keys.length - MAX_IDS; i++) delete map[keys[i]];
    }
    localStorage.setItem(storeKey(), JSON.stringify(map));
  } catch {
    // Quota / private-mode failures are silent. Ironic here — this counter exists
    // because storage failed — but a lost counter only costs extra attempts.
  }
}

export function getPartialAttempts(recordingId) {
  if (!recordingId) return 0;
  return readMap()[recordingId] || 0;
}

export function bumpPartialAttempts(recordingId) {
  if (!recordingId) return 0;
  const map = readMap();
  const next = (map[recordingId] || 0) + 1;
  map[recordingId] = next;
  writeMap(map);
  return next;
}

// True once this recording has burned its complete-retry budget: the next partial
// save must delete the rows rather than queue yet another attempt.
export function partialAttemptsExhausted(recordingId) {
  return getPartialAttempts(recordingId) >= MAX_PARTIAL_ATTEMPTS;
}

// Called when the recording finally saves COMPLETE (or its rows are dropped), so a
// recordingId can never carry a stale count into a later life.
export function clearPartialAttempts(recordingId) {
  if (!recordingId) return;
  const map = readMap();
  if (map[recordingId] != null) {
    delete map[recordingId];
    writeMap(map);
  }
}
